import {
    Colors,
    CYCLE_AUDIENCE_FILTER_LAYOUT,
    CYCLE_AUDIENCE_FILTER_TYPOGRAPHY,
    FIXED_ICON_SIZES,
    Icon,
    MultiSelectOutlined,
    SelectOutlined,
    Text,
    type MultiSelectAccent,
    type OptionProps,
} from '@amelio/ui-library';

import { resolveSelectedValueIds } from './audienceFilterValues';

import type {
    OnSelectCompanyAttributeOption,
    SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';

import { useAmelioTranslations } from '@/hooks';
import { decodeAndSanitize } from '@/utils/htmlSanitizer';
import { getLocalizedText } from '@/utils/localization';

type AudienceAttribute = OnSelectCompanyAttributeOption['attribute'];

const IDLE_BORDER = 'border border-step-rail-track';

const ROW_SURFACE = [
    'flex flex-col items-stretch border border-step-rail-track bg-white/80',
    'sm:flex-row sm:items-center',
    CYCLE_AUDIENCE_FILTER_LAYOUT.rowRadius,
    CYCLE_AUDIENCE_FILTER_LAYOUT.rowPadding,
    CYCLE_AUDIENCE_FILTER_LAYOUT.rowGap,
].join(' ');

const ATTRIBUTE_COLUMN = `w-full shrink-0 ${CYCLE_AUDIENCE_FILTER_LAYOUT.attributeWidth}`;

const REMOVE_BUTTON = [
    'group inline-flex items-center justify-center self-start transition-colors',
    'hover:bg-error/10 focus-visible:ring-primary-800 focus-visible:ring-2',
    'focus-visible:outline-none sm:self-center',
    CYCLE_AUDIENCE_FILTER_LAYOUT.removePadding,
    CYCLE_AUDIENCE_FILTER_LAYOUT.removeRadius,
].join(' ');

const REMOVE_GLYPH = 'text-step-rail-idle group-hover:text-error';

export interface AudienceFilterRowProps {
    rowId: string;
    attribute?: AudienceAttribute;
    attributeOptions: OptionProps[];
    filters: SelectedFilters;
    accent: MultiSelectAccent;
    isReadOnly: boolean;
    canRemove: boolean;
    onAttributeChange: (attributeName: string) => void;
    onRemove: () => void;
    onSelectValue: (params: OnSelectCompanyAttributeOption) => void;
}

export const AudienceFilterRow = ({
    rowId,
    attribute,
    attributeOptions,
    filters,
    accent,
    isReadOnly,
    canRemove,
    onAttributeChange,
    onRemove,
    onSelectValue,
}: AudienceFilterRowProps) => {
    const { t } = useAmelioTranslations();

    const valueOptions: OptionProps[] = (attribute?.values ?? []).map((value) => ({
        label: decodeAndSanitize('names' in value ? getLocalizedText(value.names) : value.name),
        value: value.id,
    }));

    return (
        <div
            className={ROW_SURFACE}
            data-testid={`audience-filter-row-${rowId}`}
        >
            <div className={ATTRIBUTE_COLUMN}>
                <SelectOutlined
                    options={attributeOptions}
                    value={attribute?.name}
                    placeholder={t('cycleEvaluationCycleInformation.audience.chooseAttribute')}
                    size={'md'}
                    sort={'none'}
                    readOnly={isReadOnly}
                    textClassName={CYCLE_AUDIENCE_FILTER_TYPOGRAPHY.control}
                    triggerBorderClassName={IDLE_BORDER}
                    ariaLabel={t('cycleEvaluationCycleInformation.audience.chooseAttribute')}
                    dataTestId={`audience-filter-attribute-${rowId}`}
                    onChange={onAttributeChange}
                />
            </div>

            <div className={'min-w-0 flex-1'}>
                {attribute ? (
                    <MultiSelectOutlined
                        accent={accent}
                        options={valueOptions}
                        values={resolveSelectedValueIds({ attribute, filters })}
                        label={t('cycleEvaluationCycleInformation.audience.chooseValues')}
                        showSelection={true}
                        readOnly={isReadOnly}
                        ariaLabel={decodeAndSanitize(attribute.name)}
                        dataTestId={`audience-filter-values-${rowId}`}
                        onChange={(value) => onSelectValue({ value, attribute })}
                    />
                ) : (
                    <div data-testid={`audience-filter-empty-${rowId}`}>
                        <Text
                            color={Colors.STEP_RAIL_MUTED}
                            className={CYCLE_AUDIENCE_FILTER_TYPOGRAPHY.placeholder}
                        >
                            {t('cycleEvaluationCycleInformation.audience.selectAttributeFirst')}
                        </Text>
                    </div>
                )}
            </div>

            {canRemove && !isReadOnly && (
                <button
                    type={'button'}
                    className={REMOVE_BUTTON}
                    aria-label={t('cycleEvaluationCycleInformation.audience.removeFilter')}
                    data-testid={`audience-filter-remove-${rowId}`}
                    onClick={onRemove}
                >
                    <Icon
                        name={'essential-close'}
                        size={FIXED_ICON_SIZES.sm}
                        colorClassName={REMOVE_GLYPH}
                        ariaHidden={true}
                    />
                </button>
            )}
        </div>
    );
};
